// AdminAppShellV2 — final shell.
//
// One stable layout across mobile + desktop, matching the reference
// dashboards' clean look (now visually unified with player + agent SPAs):
//   • Body background (arena-tinted #F0F7FA / arena-900) is owned by
//     each SPA's index.css — no shell-level full-viewport overlay.
//   • Top pill nav: brand mark on the left, centre tabs (desktop only),
//     utility cluster (bell / theme / avatar) on the right.
//   • Page content underneath, max-w-screen-2xl, generous padding.
//   • Bottom mobile nav remains for narrow screens.

import { ReactNode } from 'react';
import TopPillNav from './TopPillNav';
import AdminBottomNavV2 from './AdminBottomNavV2';
import AgentNavBar from './AgentNavBar';
import AgentBottomNav from './AgentBottomNav';
import { useAdminAuth } from '../lib/adminAuth';

// Phase 13.7: notifications live in the bell icon inside TopPillNav.
// The bell owns its own SSE subscription so the shell never needs to
// mount it directly.
//
// Phase 2 chrome fork (2026-05-10): when the authenticated principal
// is an agent, this shell swaps out the admin chrome (TopPillNav +
// AdminBottomNavV2) for the AgentNavBar (magenta brand) + AgentBottomNav
// (3D popped bubble with REAL cutout, section-coloured per tab). Admin
// role keeps the equivalent admin chrome — same architecture, violet
// accent instead of magenta.
//
// Phase 1 UI overhaul (2026-05-15): AuroraBackground was retired for
// both roles. The agent SPA already skipped it; admin now joins them on
// the shared arena-tinted body bg (`bg-[#F0F7FA] dark:bg-arena-900` in
// admin-frontend/src/index.css). This unifies the visual ground plane
// across all three SPAs (player / agent / admin) — same surface, same
// dark-mode response, no more cream/lilac radial spots clashing with
// dashboards.

export default function AdminAppShellV2({ children }: { children: ReactNode }) {
  const { admin, loading } = useAdminAuth();

  if (loading) {
    // Loading splash uses the same arena-tinted surface the body now
    // owns so the splash → app transition is seamless (no flash of a
    // different background colour while the auth check resolves).
    return (
      <div className="min-h-screen min-h-dvh grid place-items-center
                      bg-[#F0F7FA] dark:bg-arena-900
                      text-arena-600 dark:text-neon-200/80 text-sm">
        Loading…
      </div>
    );
  }

  const isAgent = admin?.role === 'agent';

  return (
    <div className="min-h-screen min-h-dvh text-arena-900 dark:text-neutral-100">
      {isAgent ? <AgentNavBar /> : <TopPillNav />}
      <main key={admin?.id ?? 'guest'} className="pb-28 lg:pb-12">
        {children}
      </main>
      {isAgent ? <AgentBottomNav /> : <AdminBottomNavV2 />}
    </div>
  );
}
